import { lazy, Suspense, type ReactNode } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { NotFoundPage } from '@/components/common/NotFoundPage'
import { PageLoader } from '@/components/common/PageLoader'
import { ForgotPasswordPage } from '@/features/auth/pages/ForgotPasswordPage'
import { LoginPage } from '@/features/auth/pages/LoginPage'
import { ResetPasswordPage } from '@/features/auth/pages/ResetPasswordPage'
import { Permission } from '@/features/auth/permissions'
import { AppLayout } from '@/layouts/AppLayout'
import { ClientAreaRoute } from '@/routes/ClientAreaRoute'
import { AuthLayout } from '@/layouts/AuthLayout'
import { paths } from '@/routes/paths'
import { ModuleCode } from '@/features/entitlements/types'
import { ModuleRoute } from '@/routes/ModuleRoute'
import { PermissionRoute } from '@/routes/PermissionRoute'
import { PlatformOperatorRoute } from '@/routes/PlatformOperatorRoute'
import { ProtectedRoute } from '@/routes/ProtectedRoute'
import { PublicOnlyRoute } from '@/routes/PublicOnlyRoute'
import { VersionGate } from '@/routes/VersionGate'
import { AppShellSuperAdmin } from '@/super-admin/layouts/AppShellSuperAdmin'
import { AppShellV2 } from '@/v2/layouts/AppShellV2'

// Cada módulo se carga en su propio chunk bajo demanda — reduce el bundle inicial.
const DashboardPage = lazy(() =>
  import('@/features/dashboard/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
)
const TerritorialPage = lazy(() =>
  import('@/features/territorial/pages/TerritorialPage').then((m) => ({ default: m.TerritorialPage })),
)
const PulsePage = lazy(() =>
  import('@/features/dashboard/pages/PulsePage').then((m) => ({ default: m.PulsePage })),
)
const SignalsPage = lazy(() =>
  import('@/features/dashboard/pages/SignalsPage').then((m) => ({ default: m.SignalsPage })),
)
const TopicsPage = lazy(() =>
  import('@/features/dashboard/pages/TopicsPage').then((m) => ({ default: m.TopicsPage })),
)
const EvolutionPage = lazy(() =>
  import('@/features/evolution/pages/EvolutionPage').then((m) => ({ default: m.EvolutionPage })),
)
const AiAssistantPage = lazy(() =>
  import('@/features/ai-assistant/pages/AiAssistantPage').then((m) => ({ default: m.AiAssistantPage })),
)
const DocumentsPage = lazy(() =>
  import('@/features/documents/pages/DocumentsPage').then((m) => ({ default: m.DocumentsPage })),
)
const DependenciesPage = lazy(() =>
  import('@/features/dependencies/pages/DependenciesPage').then((m) => ({ default: m.DependenciesPage })),
)
const AuditPage = lazy(() =>
  import('@/features/audit/pages/AuditPage').then((m) => ({ default: m.AuditPage })),
)
// GOVIA UI V2 — páginas del shell (Fase 2 en adelante), cada una en su
// propio chunk, igual patrón que el resto de la app.
const AhoraPage = lazy(() => import('@/v2/pages/AhoraPage').then((m) => ({ default: m.AhoraPage })))
const TerritoryPage = lazy(() => import('@/v2/pages/TerritoryPage').then((m) => ({ default: m.TerritoryPage })))
// UX-1 (GOVIA_UX_SIMPLIFICATION_AUDIT.md) — RisksPage ahora consolida
// Alertas y Predicción; PredictionsPage.tsx/AlertsPage.tsx siguen
// existiendo en disco intactos (sus rutas redirigen a /v2/risks, ver
// abajo), solo dejan de importarse aquí como componentes propios.
const RisksPage = lazy(() => import('@/v2/pages/RisksPage').then((m) => ({ default: m.RisksPage })))
// UX-4 -- DecisionsPage.tsx/RecommendationsPage.tsx siguen existiendo en
// disco intactos (sus rutas redirigen a /v2/risks, ver abajo); "Decisiones
// formales" y "Recomendaciones" ahora viven dentro de RisksPage.tsx.
const AssistantPage = lazy(() => import('@/v2/pages/AssistantPage').then((m) => ({ default: m.AssistantPage })))
const StrategyPage = lazy(() => import('@/v2/pages/StrategyPage').then((m) => ({ default: m.StrategyPage })))
// UX-3 (GOVIA_UX_SIMPLIFICATION_AUDIT.md) — ConfigurationPage ahora
// consolida Organización/Usuarios/Fuentes/Territorios/Capacidades/
// Preferencias; SourceHubPage.tsx/OrganizationPage.tsx/UsersPage.tsx/
// SettingsPage.tsx/MonitoringPage.tsx siguen existiendo en disco
// intactos (sus rutas redirigen a /configuracion o /v2/strategy, ver
// abajo; los 3 primeros se importan de nuevo, perezosamente, DENTRO de
// ConfigurationPage.tsx), solo dejan de importarse aquí como
// componentes de ruta propios.
const ConfigurationPage = lazy(() =>
  import('@/v2/pages/ConfigurationPage').then((m) => ({ default: m.ConfigurationPage })),
)
const DesignSystemPlaygroundPage = lazy(() =>
  import('@/v2/pages/DesignSystemPlaygroundPage').then((m) => ({ default: m.DesignSystemPlaygroundPage })),
)
// UX-5 (GOVIA_UX5_REPORTS_DESIGN_REPORT.md) — ReportsPage ahora V2 nativa;
// la clásica (`features/reports/pages/ReportsPage.tsx`, DataGrid + 4 tabs)
// sigue existiendo en disco intacta, solo deja de importarse aquí.
const ReportsPage = lazy(() => import('@/v2/pages/ReportsPage').then((m) => ({ default: m.ReportsPage })))
// GOVIA SUPER ADMIN (SA-3) — Platform Console, cada página en su propio
// chunk, mismo patrón que el resto de la app.
const SuperAdminOverviewPage = lazy(() =>
  import('@/super-admin/pages/OverviewPage').then((m) => ({ default: m.OverviewPage })),
)
const SuperAdminOrganizationsPage = lazy(() =>
  import('@/super-admin/pages/OrganizationsPage').then((m) => ({ default: m.OrganizationsPage })),
)
const SuperAdminOrganizationDetailPage = lazy(() =>
  import('@/super-admin/pages/OrganizationDetailPage').then((m) => ({ default: m.OrganizationDetailPage })),
)
const SuperAdminSourcesPage = lazy(() =>
  import('@/super-admin/pages/SourcesPage').then((m) => ({ default: m.SourcesPage })),
)
const SuperAdminUsersPage = lazy(() =>
  import('@/super-admin/pages/UsersPage').then((m) => ({ default: m.UsersPage })),
)
const SuperAdminHealthPage = lazy(() =>
  import('@/super-admin/pages/HealthPage').then((m) => ({ default: m.HealthPage })),
)
const SuperAdminAuditPage = lazy(() =>
  import('@/super-admin/pages/AuditPage').then((m) => ({ default: m.AuditPage })),
)
// Platform Console: módulos con pestañas (reutilizan páginas existentes).
const PlatformCostsPage = lazy(() =>
  import('@/super-admin/pages/PlatformModulePages').then((m) => ({ default: m.PlatformCostsPage })),
)
const PlansContractsPage = lazy(() =>
  import('@/super-admin/pages/PlatformModulePages').then((m) => ({ default: m.PlansContractsPage })),
)
const PlatformSettingsPage = lazy(() =>
  import('@/super-admin/pages/PlatformModulePages').then((m) => ({ default: m.PlatformSettingsPage })),
)
// GOVIA Agentic A9 — Agent Ops (un solo chunk para las páginas de Agent Ops).
const AgentOpsOverviewPage = lazy(() =>
  import('@/super-admin/agent-ops/pages').then((m) => ({ default: m.AgentOpsOverviewPage })),
)
const AgentRunsPage = lazy(() => import('@/super-admin/agent-ops/pages').then((m) => ({ default: m.AgentRunsPage })))
const AgentPipelinePage = lazy(() =>
  import('@/super-admin/agent-ops/pages').then((m) => ({ default: m.AgentPipelinePage })),
)
const AgentApprovalsPage = lazy(() =>
  import('@/super-admin/agent-ops/pages').then((m) => ({ default: m.AgentApprovalsPage })),
)
const AgentEvaluationsPage = lazy(() =>
  import('@/super-admin/agent-ops/pages').then((m) => ({ default: m.AgentEvaluationsPage })),
)
const AgentControlsPage = lazy(() =>
  import('@/super-admin/agent-ops/pages').then((m) => ({ default: m.AgentControlsPage })),
)
const AgentCostsPage = lazy(() => import('@/super-admin/agent-ops/pages').then((m) => ({ default: m.AgentCostsPage })))
const AgentOpsOrganizationPage = lazy(() =>
  import('@/super-admin/agent-ops/pages').then((m) => ({ default: m.AgentOpsOrganizationPage })),
)

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>
}

export function AppRoutes() {
  return (
    <Routes>
      <Route
        element={
          <PublicOnlyRoute>
            <AuthLayout />
          </PublicOnlyRoute>
        }
      >
        <Route path={paths.login} element={<LoginPage />} />
        <Route path={paths.forgotPassword} element={<ForgotPasswordPage />} />
        <Route path={paths.resetPassword} element={<ResetPasswordPage />} />
      </Route>

      <Route
        element={
          <ProtectedRoute>
            <ClientAreaRoute>
              <AppLayout />
            </ClientAreaRoute>
          </ProtectedRoute>
        }
      >
        <Route
          path={paths.dashboard}
          element={
            // Cutover controlado (autorizado): sirve V1 salvo que
            // VITE_GOVIA_UI_VERSION='v2' explícitamente — default seguro.
            <ModuleRoute module={ModuleCode.CENTER_OF_INTELLIGENCE}>
              <VersionGate v2Path={paths.v2}>
                <Lazy>
                  <DashboardPage />
                </Lazy>
              </VersionGate>
            </ModuleRoute>
          }
        />
        {/* Siempre V1, sin importar el switch — acceso directo de
            rollback/referencia (sección 4 del encargo de cutover). */}
        <Route
          path={paths.legacyDashboard}
          element={
            <ModuleRoute module={ModuleCode.CENTER_OF_INTELLIGENCE}>
              <Lazy>
                <DashboardPage />
              </Lazy>
            </ModuleRoute>
          }
        />
        <Route
          path={paths.territorial}
          element={
            <ModuleRoute module={ModuleCode.TERRITORIAL}>
              <Lazy>
                <TerritorialPage />
              </Lazy>
            </ModuleRoute>
          }
        />
        <Route
          path={paths.pulse}
          element={
            <ModuleRoute module={ModuleCode.CENTER_OF_INTELLIGENCE}>
              <Lazy>
                <PulsePage />
              </Lazy>
            </ModuleRoute>
          }
        />
        <Route
          path={paths.signals}
          element={
            <ModuleRoute module={ModuleCode.SIGNALS}>
              <Lazy>
                <SignalsPage />
              </Lazy>
            </ModuleRoute>
          }
        />
        <Route
          path={paths.topics}
          element={
            <ModuleRoute module={ModuleCode.CENTER_OF_INTELLIGENCE}>
              <Lazy>
                <TopicsPage />
              </Lazy>
            </ModuleRoute>
          }
        />
        <Route
          path={paths.evolution}
          element={
            <PermissionRoute permission={Permission.REPORTS_READ}>
              <ModuleRoute module={ModuleCode.EVOLUTION}>
                <Lazy>
                  <EvolutionPage />
                </Lazy>
              </ModuleRoute>
            </PermissionRoute>
          }
        />
        <Route
          path={paths.assistant}
          element={
            // Mismo permiso primero (aplica sin importar el destino), luego
            // el switch decide V1 vs. redirect a V2 (cutover autorizado).
            <PermissionRoute permission={Permission.AI_QUERY}>
              <ModuleRoute module={ModuleCode.GOVIA_AI}>
                <VersionGate v2Path={paths.v2Assistant}>
                  <Lazy>
                    <AiAssistantPage />
                  </Lazy>
                </VersionGate>
              </ModuleRoute>
            </PermissionRoute>
          }
        />
        {/* Siempre V1, sin importar el switch — acceso directo de
            rollback/referencia (sección 4 del encargo de cutover). */}
        <Route
          path={paths.legacyAssistant}
          element={
            <PermissionRoute permission={Permission.AI_QUERY}>
              <ModuleRoute module={ModuleCode.GOVIA_AI}>
                <Lazy>
                  <AiAssistantPage />
                </Lazy>
              </ModuleRoute>
            </PermissionRoute>
          }
        />
        <Route
          path={paths.documents}
          element={
            <PermissionRoute permission={Permission.DOCUMENTS_READ}>
              <ModuleRoute module={ModuleCode.EVIDENCE}>
                <Lazy>
                  <DocumentsPage />
                </Lazy>
              </ModuleRoute>
            </PermissionRoute>
          }
        />
        <Route
          path={paths.dependencies}
          element={
            <PermissionRoute permission={Permission.COMPANIES_READ}>
              <Lazy>
                <DependenciesPage />
              </Lazy>
            </PermissionRoute>
          }
        />
        {/* UX-3/UX-4 -- Fuentes/Organización/Usuarios se consolidaron
            dentro de Configuración (/configuracion, ahora
            ConfigurationPage, V2). Redirect de compatibilidad, no
            ruptura: cualquier deep-link/marcador ya guardado sigue
            funcionando. Desde UX-4, `?tab=` aterriza directamente en la
            sección correcta (sección 7 del encargo UX-4), no solo en la
            pantalla genérica -- ver ConfigurationPage.tsx. Los 3
            componentes siguen existiendo intactos (SourceHubPage.tsx/
            OrganizationPage.tsx/UsersPage.tsx), reutilizados TAL CUAL
            dentro de ConfigurationPage.tsx. */}
        <Route path={paths.sourceHub} element={<Navigate to={`${paths.settings}?tab=fuentes`} replace />} />
        <Route path={paths.organization} element={<Navigate to={paths.settings} replace />} />
        <Route path={paths.users} element={<Navigate to={`${paths.settings}?tab=usuarios`} replace />} />
        <Route
          path={paths.audit}
          element={
            <PermissionRoute permission={Permission.AUDIT_READ}>
              <Lazy>
                <AuditPage />
              </Lazy>
            </PermissionRoute>
          }
        />
      </Route>

      {/* GOVIA UI V2 (Fase 2) — mismo <ProtectedRoute> reutilizado (no una
          segunda implementación de auth), pero con AppShellV2 en vez de
          AppLayout como layout de ruta: así el shell V2 tiene su propio
          Sidebar/TopContextBar, en vez de heredar el chrome clásico. */}
      <Route
        element={
          <ProtectedRoute>
            <ClientAreaRoute>
              <AppShellV2 />
            </ClientAreaRoute>
          </ProtectedRoute>
        }
      >
        <Route
          path={paths.v2}
          element={
            <Lazy>
              <AhoraPage />
            </Lazy>
          }
        />
        <Route
          path={paths.v2Territory}
          element={
            <Lazy>
              <TerritoryPage />
            </Lazy>
          }
        />
        {/* UX-3 -- Monitoreo se retiró de la navegación cliente: sus
            "resultados útiles" (evidencia) ya viven en Inteligencia
            desde UX-2, y "Gestionar fuentes" ahora vive en
            Configuración. Redirect de compatibilidad -- MonitoringPage.tsx
            sigue existiendo intacto, no se borró código. */}
        <Route path={paths.v2Monitoring} element={<Navigate to={paths.v2Strategy} replace />} />
        <Route
          path={paths.v2Risks}
          element={
            // UX-2.6 — mismo permiso que /decision/dashboard-summary (Permission.REPORTS_READ).
            <PermissionRoute permission={Permission.REPORTS_READ}>
              <Lazy>
                <RisksPage />
              </Lazy>
            </PermissionRoute>
          }
        />
        <Route
          path={paths.v2Predictions}
          // UX-1 (GOVIA_UX_SIMPLIFICATION_AUDIT.md) — Predicción se
          // consolidó dentro de Riesgos. Redirect de compatibilidad, no
          // ruptura: cualquier deep-link/marcador ya guardado a
          // /v2/predictions sigue funcionando, solo aterriza en el
          // workspace consolidado. `PredictionsPage.tsx` sigue existiendo
          // intacto (no se borró código), solo deja de montarse aquí.
          element={<Navigate to={paths.v2Risks} replace />}
        />
        <Route
          path={paths.v2Alerts}
          // UX-1 — mismo criterio: Alertas se consolidó dentro de
          // Riesgos. `AlertsPage.tsx` sigue existiendo intacto.
          element={<Navigate to={paths.v2Risks} replace />}
        />
        {/* UX-4 -- Decision Center y Recomendaciones se consolidaron
            dentro de Riesgos ("Decisiones formales" + "Recomendaciones",
            ver RisksPage.tsx): lo genuinamente exclusivo de Decision
            Center (seguimiento con medición de impacto, NS-1) se
            reubicó ahí; la cola completa de Recomendaciones también.
            Redirect de compatibilidad -- DecisionsPage.tsx/
            RecommendationsPage.tsx siguen existiendo intactos. */}
        <Route path={paths.v2Decisions} element={<Navigate to={paths.v2Risks} replace />} />
        <Route path={paths.v2Recommendations} element={<Navigate to={paths.v2Risks} replace />} />
        <Route
          path={paths.v2Assistant}
          element={
            <PermissionRoute permission={Permission.AI_QUERY}>
              <Lazy>
                <AssistantPage />
              </Lazy>
            </PermissionRoute>
          }
        />
        <Route
          path={paths.v2Strategy}
          element={
            // Fase 8: mismo permiso que el backend exige en /decision/*
            // (Permission.REPORTS_READ, ver app/decision_center/api/endpoints.py).
            <PermissionRoute permission={Permission.REPORTS_READ}>
              <Lazy>
                <StrategyPage />
              </Lazy>
            </PermissionRoute>
          }
        />
        <Route
          path={paths.v2DesignSystem}
          element={
            <Lazy>
              <DesignSystemPlaygroundPage />
            </Lazy>
          }
        />

        {/* Fase 9 — alias cortos no destructivos (ver paths.ts). Mismo
            componente, mismo gate de permiso que su ruta /v2/* — dos URLs
            distintas hacia la misma pantalla, nunca una segunda
            implementación. */}
        <Route
          path={paths.territorio}
          element={
            <Lazy>
              <TerritoryPage />
            </Lazy>
          }
        />
        <Route
          path={paths.estrategia}
          element={
            <PermissionRoute permission={Permission.REPORTS_READ}>
              <Lazy>
                <StrategyPage />
              </Lazy>
            </PermissionRoute>
          }
        />

        {/* UX-3 -- Configuración, ahora V2 nativa (antes SettingsPage
            clásica bajo AppLayout, retirada de aquí -- sigue existiendo
            intacta en disco). Sin PermissionRoute a nivel de ruta (igual
            que la SettingsPage anterior, abierta a cualquier
            autenticado): el gating real ocurre por tab, dentro de
            ConfigurationPage.tsx, con el mismo permiso que cada
            capacidad ya exigía en su ruta original. */}
        <Route
          path={paths.settings}
          element={
            <Lazy>
              <ConfigurationPage />
            </Lazy>
          }
        />

        {/* UX-5 (GOVIA_UX5_REPORTS_DESIGN_REPORT.md) — Reportes, ahora V2
            nativa (antes ReportsPage clásica bajo AppLayout, retirada de
            aquí -- sigue existiendo intacta en disco). Mismos 2 gates que
            ya exigía la ruta original (REPORTS_READ + módulo comercial
            REPORTS) -- el gating de mutación (generar/programar/
            plantillas) vive dentro de cada panel, ver ReportsPage.tsx. */}
        <Route
          path={paths.reports}
          element={
            <PermissionRoute permission={Permission.REPORTS_READ}>
              <ModuleRoute module={ModuleCode.REPORTS}>
                <Lazy>
                  <ReportsPage />
                </Lazy>
              </ModuleRoute>
            </PermissionRoute>
          }
        />
      </Route>

      {/* GOVIA SUPER ADMIN (SA-3) — árbol de rutas completamente separado
          del cliente (nunca dentro de AppLayout/AppShellV2). Reutiliza
          <ProtectedRoute> (misma autenticación), pero además exige
          <PlatformOperatorRoute> — ningún ORG_ADMIN ni SUPER_ADMIN sin el
          flag ve jamás este shell, aunque escriban la URL a mano (y el
          backend igual sigue negando las requests por su cuenta). */}
      <Route
        element={
          <ProtectedRoute>
            <PlatformOperatorRoute>
              <AppShellSuperAdmin />
            </PlatformOperatorRoute>
          </ProtectedRoute>
        }
      >
        <Route
          path={paths.superAdmin}
          element={
            <Lazy>
              <SuperAdminOverviewPage />
            </Lazy>
          }
        />
        <Route
          path={paths.superAdminOrganizations}
          element={
            <Lazy>
              <SuperAdminOrganizationsPage />
            </Lazy>
          }
        />
        <Route
          path={paths.superAdminOrganizationDetail}
          element={
            <Lazy>
              <SuperAdminOrganizationDetailPage />
            </Lazy>
          }
        />
        {/* Ruta histórica: el consumo por organización ahora es una pestaña de Costos IA. */}
        <Route
          path={paths.superAdminAiUsage}
          element={<Navigate to={`${paths.superAdminCosts}?tab=organizations`} replace />}
        />
        <Route
          path={paths.superAdminCosts}
          element={
            <Lazy>
              <PlatformCostsPage />
            </Lazy>
          }
        />
        <Route
          path={paths.superAdminSettings}
          element={
            <Lazy>
              <PlatformSettingsPage />
            </Lazy>
          }
        />
        <Route
          path={paths.superAdminSources}
          element={
            <Lazy>
              <SuperAdminSourcesPage />
            </Lazy>
          }
        />
        <Route
          path={paths.superAdminUsers}
          element={
            <Lazy>
              <SuperAdminUsersPage />
            </Lazy>
          }
        />
        <Route
          path={paths.superAdminHealth}
          element={
            <Lazy>
              <SuperAdminHealthPage />
            </Lazy>
          }
        />
        <Route
          path={paths.superAdminAudit}
          element={
            <Lazy>
              <SuperAdminAuditPage />
            </Lazy>
          }
        />
        <Route
          path={paths.superAdminPlans}
          element={
            <Lazy>
              <PlansContractsPage />
            </Lazy>
          }
        />
        <Route
          path={paths.superAdminAgentOps}
          element={
            <Lazy>
              <AgentOpsOverviewPage />
            </Lazy>
          }
        />
        <Route
          path={paths.superAdminAgentOpsRuns}
          element={
            <Lazy>
              <AgentRunsPage />
            </Lazy>
          }
        />
        <Route
          path={paths.superAdminAgentOpsPipeline}
          element={
            <Lazy>
              <AgentPipelinePage />
            </Lazy>
          }
        />
        <Route
          path={paths.superAdminAgentOpsApprovals}
          element={
            <Lazy>
              <AgentApprovalsPage />
            </Lazy>
          }
        />
        <Route
          path={paths.superAdminAgentOpsEvaluations}
          element={
            <Lazy>
              <AgentEvaluationsPage />
            </Lazy>
          }
        />
        <Route
          path={paths.superAdminAgentOpsControls}
          element={
            <Lazy>
              <AgentControlsPage />
            </Lazy>
          }
        />
        <Route
          path={paths.superAdminAgentOpsCosts}
          element={
            <Lazy>
              <AgentCostsPage />
            </Lazy>
          }
        />
        <Route
          path={paths.superAdminAgentOpsOrganization}
          element={
            <Lazy>
              <AgentOpsOrganizationPage />
            </Lazy>
          }
        />
      </Route>

      <Route path={paths.notFound} element={<NotFoundPage />} />
    </Routes>
  )
}
